import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { authApi, customerApi } from '../services/api'
import { formatDate, parseUTC } from '../utils/dateUtils'
import CustomerLayout from '../components/customer/CustomerLayout'
import MotivationalProgress from '../components/MotivationalProgress'
import ScrapingProgress from '../components/ScrapingProgress'
import VoiceChatButton from '../components/VoiceChatButton'
import AudioPlayer from '../components/AudioPlayer'
import Agents from './Agents'
import Leads from './Leads'
import LeadCaptureSettings from './LeadCaptureSettings'
import WidgetSettings from './WidgetSettings'
import UsageCostPanel from '../components/UsageCostPanel'
import Calendar from '../components/Calendar'
import ZohoIntegration from '../components/ZohoIntegration'
import WhatsAppIntegration from '../components/WhatsAppIntegration'
import OnboardingWizard from '../components/onboarding/OnboardingWizard'
import CustomerAnalytics from './CustomerAnalytics'
import OverviewDashboard from './OverviewDashboard'
import Inbox from './Inbox'
import ManualFAQList from '../components/knowledge-base/ManualFAQList'
import HandoverSettings from '../components/customer/HandoverSettings'
import {
  KeyIcon,
  ChartBarIcon,
  DocumentIcon,
  CogIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  ClipboardIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  CloudArrowUpIcon,
  SparklesIcon,
  TrashIcon,
  CpuChipIcon,
  SpeakerWaveIcon,
  DocumentTextIcon,
  DocumentArrowUpIcon,
  PaperClipIcon,
  CalendarIcon,
  LockClosedIcon,
  StarIcon,
  LinkIcon,
  ArrowUpRightIcon,
  PlusIcon,
  EllipsisHorizontalIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  MicrophoneIcon,
  Squares2X2Icon,
  PaintBrushIcon,
  InboxArrowDownIcon,
  PresentationChartLineIcon,
  DocumentPlusIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import {
  CheckCircleIcon as CheckCircleSolid,
  ExclamationCircleIcon as ExclamationSolid,
} from '@heroicons/react/24/solid'

export default function CustomerDashboard() {
  const { user, setUser, logout } = useAuth()

  const baseUrl = typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.host}`
    : 'https://dev.cognavia.ai'

  // Core state
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab')
    const validTabs = ['overview', 'api-keys', 'knowledge-base', 'widget', 'analytics', 'inbox', 'leads', 'lead-settings', 'chat', 'calendar', 'agents', 'handover', 'channels', 'integrations', 'usage', 'settings']
    return validTabs.includes(tabParam) ? tabParam : 'overview'
  })
  const [loading, setLoading] = useState(true)

  // API Key state
  const [apiKey, setApiKey] = useState('')
  const [regenerating, setRegenerating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [embedCopied, setEmbedCopied] = useState(false)

  // Knowledge Base state
  const [knowledgeBases, setKnowledgeBases] = useState([])
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [knowledgeBaseName, setKnowledgeBaseName] = useState('')
  const [isScrapingWebsite, setIsScrapingWebsite] = useState(false)
  const [scrapingProgress, setScrapingProgress] = useState(0)
  const [scrapingStatus, setScrapingStatus] = useState('')
  const [scrapingStage, setScrapingStage] = useState('starting')
  const [showMotivationalProgress, setShowMotivationalProgress] = useState(false)
  const [lastCompletedKB, setLastCompletedKB] = useState(null)
  const [deletingKBId, setDeletingKBId] = useState(null)
  const [recrawlingKBId, setRecrawlingKBId] = useState(null)

  // Document Upload state
  const [selectedFile, setSelectedFile] = useState(null)
  const [documentName, setDocumentName] = useState('')
  const [documentDescription, setDocumentDescription] = useState('')
  const [isUploadingDocument, setIsUploadingDocument] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [activeKBTab, setActiveKBTab] = useState('website')

  // Chat state
  const [chatMessages, setChatMessages] = useState([])
  const [currentMessage, setCurrentMessage] = useState('')
  const [isChatLoading, setIsChatLoading] = useState(false)
  const [userName, setUserName] = useState(localStorage.getItem('chat_user_name') || '')
  const [showNamePrompt, setShowNamePrompt] = useState(!localStorage.getItem('chat_user_name'))
  const chatEndRef = useRef(null)

  // Usage tracking state
  const [totalChatSessions, setTotalChatSessions] = useState(0)

  // Voice chat state
  const [voiceEnabled, setVoiceEnabled] = useState(false)
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null)
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false)
  const [voiceSettingsEnabled, setVoiceSettingsEnabled] = useState(false)
  const [voiceSettingsLoading, setVoiceSettingsLoading] = useState(false)

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [onboardingChecked, setOnboardingChecked] = useState(false)

  // Feedback state
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Premium feature lock state
  const [showPremiumModal, setShowPremiumModal] = useState(false)
  const [premiumFeatureName, setPremiumFeatureName] = useState('')

  // ============================================================
  // Data Fetching
  // ============================================================

  useEffect(() => { fetchInitialData() }, [])

  // Re-fetch usage data when switching to overview tab
  useEffect(() => {
    if (activeTab === 'overview') {
      fetchUsageSummary()
    }
  }, [activeTab])

  useEffect(() => {
    const interval = setInterval(() => setChatMessages(prev => [...prev]), 30000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages, isChatLoading])

  const fetchInitialData = async () => {
    setLoading(true)
    try {
      await Promise.allSettled([
        fetchApiKey(), fetchKnowledgeBases(), fetchUserStats(),
        fetchVoiceSettings(), fetchUsageSummary(),
        checkOnboarding()
      ])
    } catch (err) {
      console.error('Failed to fetch initial data:', err)
    } finally {
      setLoading(false)
    }
  }

  const checkOnboarding = async () => {
    try {
      const response = await customerApi.get('/customer/onboarding/status')
      if (response.data && !response.data.onboarding_completed) {
        setShowOnboarding(true)
      }
    } catch (err) {
      console.log('Onboarding check skipped:', err.message)
    } finally {
      setOnboardingChecked(true)
    }
  }

  const fetchApiKey = async () => {
    try {
      const userToken = localStorage.getItem('auth_token')
      if (!userToken) return
      const response = await authApi.get('/auth/api-key')
      setApiKey(response.data.apiKey || '')
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Email verification required to view your API key.')
        setTimeout(() => setError(''), 5000)
      }
    }
  }

  const fetchKnowledgeBases = async () => {
    try {
      const response = await customerApi.get('/customer/knowledge-base/list')
      const kbData = response.data
      if (Array.isArray(kbData)) setKnowledgeBases(kbData)
      else if (kbData && Array.isArray(kbData.knowledge_bases)) setKnowledgeBases(kbData.knowledge_bases)
      else setKnowledgeBases([])
    } catch { setKnowledgeBases([]) }
  }

  const fetchUserStats = async () => {
    try {
      const response = await authApi.get('/auth/dashboard')
      const dashboardData = response.data
      if (dashboardData?.subscription?.plan && user) {
        const serverPlan = dashboardData.subscription.plan
        if (serverPlan !== user.subscriptionPlan) {
          const updatedUser = { ...user, subscriptionPlan: serverPlan }
          setUser(updatedUser)
          localStorage.setItem('user_data', JSON.stringify(updatedUser))
        }
      }
    } catch {}
  }

  const fetchVoiceSettings = async () => {
    try {
      const response = await authApi.getVoiceSettings()
      setVoiceSettingsEnabled(response.data.voice_enabled || false)
    } catch { setVoiceSettingsEnabled(false) }
  }

  const fetchUsageSummary = async () => {
    try {
      const response = await customerApi.get('/customer/usage/summary')
      setTotalChatSessions(response.data.total_conversations || 0)
    } catch {}
  }

  // ============================================================
  // Action Handlers
  // ============================================================

  const handleToggleVoice = async () => {
    try {
      setVoiceSettingsLoading(true)
      const newValue = !voiceSettingsEnabled
      await authApi.toggleVoice(newValue)
      setVoiceSettingsEnabled(newValue)
      setSuccess(`Voice chat ${newValue ? 'enabled' : 'disabled'}`)
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update voice settings')
      setTimeout(() => setError(''), 5000)
    } finally {
      setVoiceSettingsLoading(false)
    }
  }

  const regenerateApiKey = async () => {
    try {
      setRegenerating(true)
      const userToken = localStorage.getItem('auth_token')
      if (!userToken) { setError('No authentication token found.'); return }
      const response = await authApi.post('/auth/regenerate-api-key', {})
      setApiKey(response.data.apiKey)
      setSuccess('API key regenerated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      if (err.response?.status === 403) setError('Email verification required.')
      else setError(err.response?.data?.detail || 'Failed to regenerate API key.')
      setTimeout(() => setError(''), 5000)
    } finally {
      setRegenerating(false)
    }
  }

  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const copyEmbedCode = async () => {
    const code = `<script src="${baseUrl}/embed.js" data-api-key="${apiKey}"></script>`
    try {
      await navigator.clipboard.writeText(code)
      setEmbedCopied(true)
      setTimeout(() => setEmbedCopied(false), 2000)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = code; ta.style.position = 'fixed'; ta.style.left = '-9999px'
      document.body.appendChild(ta); ta.select(); document.execCommand('copy')
      document.body.removeChild(ta)
      setEmbedCopied(true)
      setTimeout(() => setEmbedCopied(false), 2000)
    }
  }

  const formatWhatsAppTime = (timestamp) => {
    if (!timestamp) return 'Just now'
    const msgDate = new Date(timestamp)
    const now = new Date()
    const diffMs = now - msgDate
    const diffMins = Math.floor(diffMs / 60000)
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (msgDate.getDate() === now.getDate()) {
      return msgDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    }
    const diffDays = Math.floor(diffMs / 86400000)
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return msgDate.toLocaleDateString('en-US', { weekday: 'long' })
    return msgDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const handleWebsiteScraping = async () => {
    if (!websiteUrl || !knowledgeBaseName) {
      setError('Please provide both website URL and knowledge base name')
      setTimeout(() => setError(''), 3000); return
    }
    try {
      setIsScrapingWebsite(true); setScrapingProgress(0); setScrapingStage('starting')
      setScrapingStatus('Starting website scraping...'); setShowMotivationalProgress(true)
      setTimeout(() => { setScrapingProgress(15); setScrapingStage('scraping'); setScrapingStatus('Extracting content from website...') }, 800)
      setTimeout(() => { setScrapingProgress(35); setScrapingStage('analyzing'); setScrapingStatus('Analyzing and processing content...') }, 2000)
      setTimeout(() => { setScrapingProgress(60); setScrapingStage('embedding'); setScrapingStatus('Creating AI embeddings...') }, 3500)

      const response = await customerApi.post('/customer/knowledge-base/scrape-website', {
        url: websiteUrl, name: knowledgeBaseName,
        description: `Knowledge base created from ${websiteUrl}`, use_js: null
      }, { timeout: 300000 })

      setScrapingProgress(85); setScrapingStage('indexing'); setScrapingStatus('Storing in knowledge base...')

      if (response.data.success) {
        setScrapingProgress(100); setScrapingStage('completing')
        const chunksAdded = response.data.data.chunks_added || response.data.data.chunk_count || 0
        const newKB = {
          id: response.data.data.knowledge_base_id, name: knowledgeBaseName,
          description: response.data.data.description || response.data.data.title || `Scraped from ${websiteUrl}`,
          document_count: chunksAdded, created_at: new Date().toISOString(),
          url: websiteUrl, method: response.data.data.method,
          knowledge_base_id: response.data.data.knowledge_base_id
        }
        setKnowledgeBases(prev => Array.isArray(prev) ? [newKB, ...prev] : [newKB])
        setLastCompletedKB(newKB)
        setSuccess(`Knowledge base "${knowledgeBaseName}" created with ${chunksAdded} chunks!`)
        setWebsiteUrl(''); setKnowledgeBaseName('')
      } else throw new Error(response.data.error || 'Scraping failed')
    } catch (err) {
      setScrapingProgress(0); setScrapingStage('error')
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Scraping timed out. The website may be too large. Try a smaller site.')
        setTimeout(() => fetchKnowledgeBases(), 5000)
      } else {
        const msg = err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to scrape website'
        setError(msg)
      }
    } finally {
      setTimeout(() => { setIsScrapingWebsite(false); setShowMotivationalProgress(false) }, 3000)
      setTimeout(() => { setSuccess(''); setError(''); setScrapingStatus(''); setScrapingProgress(0); setScrapingStage('starting') }, 8000)
    }
  }

  const handleDocumentUpload = async () => {
    if (!selectedFile || !documentName) {
      setError('Please select a file and provide a name'); setTimeout(() => setError(''), 3000); return
    }
    try {
      setIsUploadingDocument(true); setUploadProgress(0)
      const formData = new FormData()
      formData.append('file', selectedFile); formData.append('name', documentName)
      if (documentDescription) formData.append('description', documentDescription)
      setUploadProgress(20)

      const response = await customerApi.post('/customer/knowledge-base/upload-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000,
        onUploadProgress: (e) => setUploadProgress(20 + Math.round((e.loaded * 60) / e.total))
      })

      setUploadProgress(90)
      if (response.data.success) {
        setUploadProgress(100)
        const chunksAdded = response.data.data.chunks_added || 0
        const newKB = {
          id: response.data.data.knowledge_base_id, name: documentName,
          description: documentDescription || `Uploaded ${selectedFile.name}`,
          document_count: chunksAdded, created_at: new Date().toISOString(),
          source_type: 'document', file_name: selectedFile.name,
          file_type: response.data.data.file_type, file_size_mb: response.data.data.file_size_mb,
        }
        setKnowledgeBases(prev => Array.isArray(prev) ? [newKB, ...prev] : [newKB])
        setLastCompletedKB(newKB)
        setSuccess(`Document "${documentName}" uploaded with ${chunksAdded} chunks!`)
        setSelectedFile(null); setDocumentName(''); setDocumentDescription('')
        const fi = document.getElementById('document-file-input')
        if (fi) fi.value = ''
      } else throw new Error(response.data.error || 'Upload failed')
    } catch (err) {
      setUploadProgress(0)
      setError(err.response?.data?.detail || err.message || 'Failed to upload document')
    } finally {
      setTimeout(() => { setIsUploadingDocument(false); setUploadProgress(0) }, 2000)
      setTimeout(() => { setSuccess(''); setError('') }, 8000)
    }
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (!file) return
    if (file.size > 50 * 1024 * 1024) {
      setError(`File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB (max 50MB)`)
      setTimeout(() => setError(''), 5000); return
    }
    const ext = file.name.split('.').pop().toLowerCase()
    if (!['pdf','docx','doc','txt','md','markdown','csv','json','pptx','ppt'].includes(ext)) {
      setError(`Unsupported file type: .${ext}`)
      setTimeout(() => setError(''), 5000); return
    }
    setSelectedFile(file)
    if (!documentName) setDocumentName(file.name.substring(0, file.name.lastIndexOf('.')))
  }

  const handleNameSubmit = (name) => {
    if (name.trim()) {
      localStorage.setItem('chat_user_name', name.trim())
      setUserName(name.trim()); setShowNamePrompt(false)
      setChatMessages([{
        role: 'assistant',
        content: `Hi ${name.trim()}! I'm here to help answer questions about your knowledge base. What would you like to know?`,
        timestamp: new Date().toISOString()
      }])
    }
  }

  const sendChatMessage = async () => {
    if (!currentMessage.trim()) return
    const userMessage = { role: 'user', content: currentMessage, timestamp: new Date().toISOString() }
    setChatMessages(prev => [...prev, userMessage])
    const msgText = currentMessage
    setCurrentMessage(''); setIsChatLoading(true)
    try {
      const response = await customerApi.post('/customer/chat/agent', {
        message: msgText,
        session_id: 'dashboard',
        chat_history: chatMessages.map(m => ({ role: m.role, content: m.content })),
      })
      const answer = response.data.response
      setChatMessages(prev => [...prev, {
        role: 'assistant', content: answer, agent_type: response.data.agent_type, timestamp: new Date().toISOString()
      }])
      if (voiceEnabled && answer) await generateVoiceResponse(answer)
      // Re-fetch usage summary after successful chat (async tracking may take a moment)
      setTimeout(() => fetchUsageSummary(), 2000)
    } catch (err) {
      let errorContent = 'Sorry, I encountered an error processing your message.'
      if (err.response?.data?.detail) errorContent += `\n\n${err.response.data.detail}`
      setChatMessages(prev => [...prev, { role: 'assistant', content: errorContent, timestamp: new Date().toISOString() }])
    } finally { setIsChatLoading(false) }
  }

  const handleVoiceTranscript = (transcript) => {
    setCurrentMessage(transcript)
    setTimeout(() => { if (transcript.trim()) sendChatMessage() }, 500)
  }

  const generateVoiceResponse = async (text) => {
    if (!text || !voiceEnabled) return
    try {
      setIsGeneratingAudio(true)
      if (currentAudioUrl) { URL.revokeObjectURL(currentAudioUrl); setCurrentAudioUrl(null) }
      const response = await customerApi.post('/customer/voice/text-to-speech',
        { text, voice_id: null, model_id: 'eleven_monolingual_v1' },
        { responseType: 'blob' }
      )
      setCurrentAudioUrl(URL.createObjectURL(new Blob([response.data], { type: 'audio/mpeg' })))
    } catch { setError('Failed to generate voice response'); setTimeout(() => setError(''), 3000) }
    finally { setIsGeneratingAudio(false) }
  }

  const toggleVoiceMode = () => {
    setVoiceEnabled(!voiceEnabled)
    if (voiceEnabled && currentAudioUrl) { URL.revokeObjectURL(currentAudioUrl); setCurrentAudioUrl(null) }
  }

  useEffect(() => {
    return () => { if (currentAudioUrl) URL.revokeObjectURL(currentAudioUrl) }
  }, [currentAudioUrl])

  const deleteKnowledgeBase = async (kbId, kbName) => {
    if (!confirm(`Are you sure you want to delete "${kbName}"? This cannot be undone.`)) return
    try {
      setDeletingKBId(kbId)
      const response = await customerApi.delete(`/customer/knowledge-base/${kbId}`)
      if (response.data.success) {
        setKnowledgeBases(prev => Array.isArray(prev) ? prev.filter(kb => kb.id !== kbId) : [])
        setSuccess(`Deleted "${kbName}" and ${response.data.data.deleted_chunks_count} chunks`)
        setTimeout(() => setSuccess(''), 4000)
      } else throw new Error('Failed to delete')
    } catch (err) {
      setError(`Failed to delete "${kbName}": ${err.response?.data?.detail || err.message}`)
      setTimeout(() => setError(''), 5000)
    } finally { setDeletingKBId(null) }
  }

  // Phase D: manual re-crawl. Replaces all chunks for this website KB
  // with a fresh crawl of its original URL. Backend returns a job_id
  // instantly; a progress-stream UX can be layered on later.
  const handleRecrawl = async (kb) => {
    if (!kb?.id) return
    if (!confirm(`Re-crawl "${kb.name}"?\n\nAll existing chunks will be REPLACED with fresh content.`)) return
    setRecrawlingKBId(kb.id)
    try {
      const r = await customerApi.post(`/customer/knowledge-base/knowledge-base/${kb.id}/recrawl`, {})
      setSuccess(`Re-crawl queued for "${kb.name}". New content will appear shortly.`)
      setTimeout(() => setSuccess(''), 5000)
      // Lightweight refresh after a short delay so the UI shows the
      // "processing" status and refreshed timestamps. Full progress SSE
      // can be wired in later.
      setTimeout(() => { try { loadKnowledgeBases?.() } catch {} }, 2000)
    } catch (err) {
      setError(`Could not re-crawl "${kb.name}": ${err.response?.data?.detail || err.message}`)
      setTimeout(() => setError(''), 6000)
    } finally {
      setRecrawlingKBId(null)
    }
  }

  // "Last synced 3h ago" / "yesterday" etc.
  const _formatSynced = (iso) => {
    if (!iso) return 'never'
    const d = new Date(iso)
    if (isNaN(d.getTime())) return 'unknown'
    const diffMs = Date.now() - d.getTime()
    if (diffMs < 60_000) return 'just now'
    if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`
    if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`
    if (diffMs < 7 * 86_400_000) return `${Math.floor(diffMs / 86_400_000)}d ago`
    return d.toLocaleDateString()
  }

  // ============================================================
  // Tab configuration
  // ============================================================

  const isPremiumUser = ['basic', 'growth', 'pro', 'premium', 'professional', 'enterprise'].includes(user?.subscriptionPlan)
  // Handover is admin-gated: the super admin toggles handover_enabled on a
  // customer, and only THEN does the Handover tab appear in that customer's
  // dashboard. The flag is populated into the user object by the /auth/login
  // response (see backend/api/auth/auth.py). Hide the tab (not disabled) so
  // customers without the feature don't see the entry at all.
  const handoverEnabled = Boolean(user?.handoverEnabled)

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Squares2X2Icon, isPremium: false },
    { id: 'api-keys', name: 'API Keys', icon: KeyIcon, isPremium: false },
    { id: 'knowledge-base', name: 'Knowledge Base', icon: DocumentIcon, isPremium: false },
    { id: 'widget', name: 'Widget', icon: PaintBrushIcon, isPremium: false },
    { id: 'analytics', name: 'Analytics', icon: PresentationChartLineIcon, isPremium: false },
    { id: 'inbox', name: 'Inbox', icon: InboxArrowDownIcon, isPremium: false },
    { id: 'leads', name: 'Leads', icon: UserGroupIcon, isPremium: false },
    { id: 'chat', name: 'AI Chat', icon: ChatBubbleLeftRightIcon, isPremium: false },
    { id: 'calendar', name: 'Calendar', icon: CalendarIcon, isPremium: !isPremiumUser },
    { id: 'agents', name: 'AI Agents', icon: CpuChipIcon, isPremium: !isPremiumUser },
    ...(handoverEnabled ? [{ id: 'handover', name: 'Handover', icon: UserGroupIcon, isPremium: false }] : []),
    { id: 'channels', name: 'Channels', icon: GlobeAltIcon, isPremium: false },
    { id: 'integrations', name: 'Integrations', icon: LinkIcon, isPremium: false },
    { id: 'usage', name: 'Usage & Costs', icon: ChartBarIcon, isPremium: !isPremiumUser },
    { id: 'settings', name: 'Settings', icon: CogIcon, isPremium: false }
  ]

  const handleTabClick = (tab) => {
    if (tab.isPremium) { setPremiumFeatureName(tab.name); setShowPremiumModal(true) }
    else setActiveTab(tab.id)
  }

  // ============================================================
  // Loading / Verification States
  // ============================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mx-auto" />
          <p className="text-slate-500 text-sm">Loading your dashboard...</p>
        </div>
      </div>
    )
  }

  if (user && !user.isVerified) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Email Verification Required</h2>
          <p className="text-slate-500 mb-6">Please verify your email address to access your dashboard.</p>
          <p className="text-sm text-slate-400 mb-6">{user.email}</p>
          <button onClick={() => window.location.href = '/login'}
            className="w-full bg-brand-600 text-white px-4 py-2.5 rounded-xl hover:bg-brand-700 font-medium transition-colors">
            Go to Login
          </button>
          <button onClick={logout} className="mt-3 text-sm text-slate-400 hover:text-slate-600">Logout</button>
        </div>
      </div>
    )
  }

  // ============================================================
  // Computed values
  // ============================================================

  const kbCount = Array.isArray(knowledgeBases) ? knowledgeBases.length : 0

  // ============================================================
  // Render
  // ============================================================

  return (
    <CustomerLayout
      activeTab={activeTab}
      tabs={tabs}
      onTabClick={handleTabClick}
      user={user}
      onLogout={logout}
    >
      {/* Toast notifications */}
      {(success || error) && (
        <div className="fixed top-4 right-4 z-50 max-w-sm animate-slide-in">
          {success && (
            <div className="flex items-start space-x-3 bg-white border border-emerald-200 rounded-xl p-4 shadow-lg mb-2">
              <CheckCircleSolid className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">Success</p>
                <p className="text-sm text-slate-500 mt-0.5">{success}</p>
              </div>
              <button onClick={() => setSuccess('')} className="text-slate-400 hover:text-slate-600"><XMarkIcon className="h-4 w-4" /></button>
            </div>
          )}
          {error && (
            <div className="flex items-start space-x-3 bg-white border border-red-200 rounded-xl p-4 shadow-lg">
              <ExclamationSolid className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">Error</p>
                <p className="text-sm text-slate-500 mt-0.5">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-slate-400 hover:text-slate-600"><XMarkIcon className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      )}

      {/* ======================== OVERVIEW ======================== */}
      {activeTab === 'overview' && (
        <OverviewDashboard
          user={user}
          apiKey={apiKey}
          knowledgeBases={knowledgeBases}
          onNavigate={(tab) => setActiveTab(tab)}
        />
      )}

      {/* ======================== API KEYS ======================== */}
      {activeTab === 'api-keys' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Your API Key</h3>
                <p className="text-sm text-slate-500 mt-0.5">Use this key to authenticate your chatbot widget.</p>
              </div>
              <button onClick={regenerateApiKey} disabled={regenerating}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-xl hover:bg-slate-800 disabled:opacity-50 transition-colors">
                <ArrowPathIcon className={`h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
                <span>{regenerating ? 'Generating...' : 'Regenerate'}</span>
              </button>
            </div>

            {apiKey ? (
              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">API Key</label>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-mono text-sm text-slate-700 overflow-x-auto">
                      {apiKey}
                    </div>
                    <button onClick={copyApiKey}
                      className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                        copied ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}>
                      {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardIcon className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Embed Code */}
                <div className="bg-slate-900 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-white">One-Line Integration</h4>
                      <p className="text-xs text-slate-400 mt-0.5">Add this before &lt;/body&gt; in your HTML</p>
                    </div>
                    <button onClick={copyEmbedCode}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        embedCopied ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white hover:bg-white/20'
                      }`}>
                      {embedCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="text-emerald-400 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
{`<script src="${baseUrl}/embed.js" data-api-key="${apiKey}"></script>`}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <KeyIcon className="h-7 w-7 text-slate-400" />
                </div>
                <p className="text-slate-500 mb-4">No API key found. Generate one to get started.</p>
                <button onClick={regenerateApiKey} disabled={regenerating}
                  className="px-6 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-xl hover:bg-brand-700 transition-colors">
                  Generate API Key
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ======================== KNOWLEDGE BASE ======================== */}
      {activeTab === 'knowledge-base' && (
        <div className="space-y-6">
          {/* Create KB */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-base font-semibold text-slate-900 mb-4">Create Knowledge Base</h3>

            {/* Source toggle */}
            <div className="flex space-x-1 mb-6 bg-slate-100 p-1 rounded-xl">
              <button onClick={() => setActiveKBTab('website')}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeKBTab === 'website' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>
                <GlobeAltIcon className="h-4 w-4" /><span>Scrape Website</span>
              </button>
              <button onClick={() => setActiveKBTab('document')}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeKBTab === 'document'
                    ? isPremiumUser ? 'bg-white text-slate-900 shadow-sm' : 'bg-amber-50 text-amber-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}>
                <DocumentArrowUpIcon className="h-4 w-4" /><span>Upload Document</span>
                {!isPremiumUser && <LockClosedIcon className="h-3.5 w-3.5 text-amber-500" />}
              </button>
              <button onClick={() => setActiveKBTab('manual-faq')}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeKBTab === 'manual-faq' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>
                <DocumentPlusIcon className="h-4 w-4" /><span>Manual FAQs</span>
              </button>
            </div>

            {/* Website form */}
            {activeKBTab === 'website' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Name</label>
                    <input type="text" value={knowledgeBaseName} onChange={(e) => setKnowledgeBaseName(e.target.value)}
                      placeholder="e.g., Company Docs"
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Website URL</label>
                    <input type="text" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="www.example.com"
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all" />
                  </div>
                </div>
                <button onClick={handleWebsiteScraping} disabled={isScrapingWebsite || !websiteUrl || !knowledgeBaseName}
                  className="flex items-center space-x-2 px-5 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-xl hover:bg-brand-700 disabled:opacity-50 transition-colors">
                  {isScrapingWebsite
                    ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /><span>Scraping...</span></>
                    : <><GlobeAltIcon className="h-4 w-4" /><span>Scrape Website</span></>
                  }
                </button>

                {isScrapingWebsite && (
                  <div className="space-y-4 mt-4">
                    <ScrapingProgress isActive={isScrapingWebsite} currentStage={scrapingStage}
                      overallProgress={scrapingProgress} websiteUrl={websiteUrl} />
                    <MotivationalProgress isVisible={showMotivationalProgress} stage={scrapingStage}
                      progress={scrapingProgress} customMessage={scrapingStatus} duration={30} showFunFacts={true} />
                  </div>
                )}
              </div>
            )}

            {/* Document upload form */}
            {activeKBTab === 'document' && (
              isPremiumUser ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Document Name</label>
                      <input type="text" value={documentName} onChange={(e) => setDocumentName(e.target.value)}
                        placeholder="e.g., Company Handbook"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Description (optional)</label>
                      <input type="text" value={documentDescription} onChange={(e) => setDocumentDescription(e.target.value)}
                        placeholder="Brief description"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">File</label>
                    <input id="document-file-input" type="file" accept=".pdf,.docx,.doc,.txt,.md,.csv,.json,.pptx"
                      onChange={handleFileSelect}
                      className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100" />
                    <p className="text-xs text-slate-400 mt-1">PDF, DOCX, TXT, MD, CSV, JSON, PPTX (max 50MB)</p>
                  </div>
                  {isUploadingDocument && (
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div className="bg-brand-600 h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  )}
                  <button onClick={handleDocumentUpload} disabled={isUploadingDocument || !selectedFile || !documentName}
                    className="flex items-center space-x-2 px-5 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-xl hover:bg-brand-700 disabled:opacity-50 transition-colors">
                    {isUploadingDocument
                      ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /><span>Uploading... {uploadProgress}%</span></>
                      : <><CloudArrowUpIcon className="h-4 w-4" /><span>Upload Document</span></>
                    }
                  </button>
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <LockClosedIcon className="h-8 w-8 text-amber-500" />
                  </div>
                  <h4 className="text-base font-semibold text-slate-900 mb-1">Premium Feature</h4>
                  <p className="text-sm text-slate-500 mb-4 max-w-xs mx-auto">Document upload is available for Premium and Enterprise plans.</p>
                  <button onClick={() => { setPremiumFeatureName('Document Upload'); setShowPremiumModal(true) }}
                    className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all">
                    <StarIcon className="h-4 w-4 inline mr-1.5" />Learn About Premium
                  </button>
                </div>
              )
            )}

            {/* Manual FAQ sub-tab */}
            {activeKBTab === 'manual-faq' && (
              <ManualFAQList />
            )}
          </div>

          {/* KB List */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Your Knowledge Bases</h3>
                <p className="text-sm text-slate-500">{kbCount} knowledge base{kbCount !== 1 ? 's' : ''}</p>
              </div>
              {kbCount > 0 && (
                <span className="flex items-center space-x-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                  <SparklesIcon className="h-3.5 w-3.5" /><span>AI Ready</span>
                </span>
              )}
            </div>

            {kbCount > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {knowledgeBases.map((kb, i) => {
                  const isDoc = kb.source_type === 'document'
                  const isNew = lastCompletedKB && kb.name === lastCompletedKB.name
                  // Phase D: compute "last synced" from processed_at / updated_at
                  const lastSyncedIso = kb.processed_at || kb.updated_at || kb.created_at
                  const lastSyncedLabel = lastSyncedIso ? _formatSynced(lastSyncedIso) : ''
                  const canRecrawl = !isDoc && kb.url  // only website KBs are re-crawlable
                  const isRecrawling = recrawlingKBId === kb.id
                  return (
                    <div key={i} className={`group rounded-xl border p-4 transition-all hover:shadow-md ${
                      isNew ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 hover:border-slate-300'
                    }`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2.5">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                            isDoc ? 'bg-purple-100' : 'bg-blue-100'
                          }`}>
                            {isDoc
                              ? <DocumentTextIcon className="h-4.5 w-4.5 text-purple-600" />
                              : <GlobeAltIcon className="h-4.5 w-4.5 text-blue-600" />
                            }
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-sm font-semibold text-slate-900 truncate">{kb.name || `KB ${i + 1}`}</h4>
                            <span className={`text-[11px] font-medium ${isDoc ? 'text-purple-600' : 'text-blue-600'}`}>
                              {isDoc ? (kb.file_type?.toUpperCase() || 'DOC') : 'Website'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {canRecrawl && (
                            <button
                              onClick={() => handleRecrawl(kb)}
                              disabled={isRecrawling}
                              title="Re-crawl this website now (replaces all chunks)"
                              className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all disabled:opacity-50"
                            >
                              <ArrowPathIcon className={`h-3.5 w-3.5 ${isRecrawling ? 'animate-spin' : ''}`} />
                            </button>
                          )}
                          <button onClick={() => deleteKnowledgeBase(kb.id, kb.name)} disabled={deletingKBId === kb.id}
                            className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50">
                            {deletingKBId === kb.id ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <TrashIcon className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mb-3 line-clamp-2">{kb.description || 'No description'}</p>
                      <div className="flex items-center justify-between text-[11px] text-slate-400">
                        <span>{kb.document_count || 0} chunks</span>
                        <span title={lastSyncedIso || ''}>
                          {canRecrawl ? `Last synced ${lastSyncedLabel}` : (kb.created_at ? formatDate(kb.created_at) : 'Recently')}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <DocumentIcon className="h-7 w-7 text-slate-300" />
                </div>
                <h4 className="text-sm font-medium text-slate-900 mb-1">No knowledge bases yet</h4>
                <p className="text-xs text-slate-500">Scrape a website or upload a document to get started.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ======================== ANALYTICS ======================== */}
      {activeTab === 'analytics' && (
        <CustomerAnalytics />
      )}

      {/* ======================== INBOX ======================== */}
      {activeTab === 'inbox' && (
        <Inbox />
      )}

      {/* ======================== AI CHAT ======================== */}
      {activeTab === 'chat' && (
        <div className="flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden" style={{ height: 'calc(100vh - 160px)' }}>
          {/* Chat header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-xl bg-brand-600 flex items-center justify-center">
                <span className="text-white text-sm font-bold">C</span>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">CognaAssist AI</h3>
                <p className="text-xs text-slate-500 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full inline-block" />
                  <span>Online</span>
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={toggleVoiceMode}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  voiceEnabled ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}>
                <MicrophoneIcon className="h-3.5 w-3.5 inline mr-1" />
                {voiceEnabled ? 'Voice On' : 'Voice Off'}
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {showNamePrompt && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center max-w-sm p-6 bg-slate-50 rounded-2xl border border-slate-200">
                  <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <ChatBubbleLeftRightIcon className="h-6 w-6 text-brand-600" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 mb-1">What should I call you?</h3>
                  <p className="text-sm text-slate-500 mb-4">This helps me personalize our conversation.</p>
                  <form onSubmit={(e) => { e.preventDefault(); handleNameSubmit(e.target.elements.userName.value) }}>
                    <input type="text" name="userName" placeholder="Your name" autoFocus
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-brand-500" />
                    <button type="submit" className="w-full px-4 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 transition-colors">
                      Start Chatting
                    </button>
                  </form>
                  <button onClick={() => {
                    setShowNamePrompt(false)
                    setChatMessages([{ role: 'assistant', content: "Hi there! I'm here to help. What would you like to know?", timestamp: new Date().toISOString() }])
                  }} className="mt-3 text-xs text-slate-400 hover:text-slate-600">Skip</button>
                </div>
              </div>
            )}

            {!showNamePrompt && chatMessages.length === 0 && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <ChatBubbleLeftRightIcon className="h-7 w-7 text-slate-300" />
                  </div>
                  <p className="text-sm text-slate-500">Ask me anything about your knowledge base.</p>
                </div>
              </div>
            )}

            {!showNamePrompt && chatMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-end space-x-2 max-w-[80%] ${msg.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' ? 'bg-slate-200' : 'bg-brand-600'
                  }`}>
                    <span className={`text-xs font-semibold ${msg.role === 'user' ? 'text-slate-600' : 'text-white'}`}>
                      {msg.role === 'user' ? (userName?.[0]?.toUpperCase() || 'U') : 'C'}
                    </span>
                  </div>
                  <div className={`px-4 py-2.5 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-brand-600 text-white rounded-br-md'
                      : 'bg-slate-100 text-slate-800 rounded-bl-md'
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-[10px] mt-1.5 ${msg.role === 'user' ? 'text-white/60' : 'text-slate-400'}`}>
                      {formatWhatsAppTime(msg.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {!showNamePrompt && isChatLoading && (
              <div className="flex justify-start">
                <div className="flex items-end space-x-2">
                  <div className="w-7 h-7 rounded-lg bg-brand-600 flex items-center justify-center">
                    <span className="text-xs font-semibold text-white">C</span>
                  </div>
                  <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex space-x-1.5">
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input area */}
          <div className="border-t border-slate-200 p-4 bg-white space-y-3">
            {voiceEnabled && currentAudioUrl && <AudioPlayer audioUrl={currentAudioUrl} autoPlay={true} />}
            <div className="flex items-end space-x-3">
              <div className="flex-1">
                <input type="text" value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                  placeholder="Type your message..."
                  disabled={isChatLoading}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:bg-slate-50 transition-all" />
              </div>
              {voiceEnabled && (
                <VoiceChatButton onTranscript={handleVoiceTranscript} disabled={isChatLoading} isProcessing={isChatLoading || isGeneratingAudio} />
              )}
              <button onClick={sendChatMessage} disabled={!currentMessage.trim() || isChatLoading}
                className="px-4 py-2.5 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:opacity-50 transition-colors">
                <PaperAirplaneIcon className="h-4 w-4" />
              </button>
            </div>
            {voiceEnabled && isGeneratingAudio && (
              <div className="flex items-center justify-center space-x-1.5 text-xs text-brand-600">
                <div className="w-1.5 h-1.5 bg-brand-500 rounded-full animate-ping" />
                <span>Generating voice response...</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ======================== WIDGET SETTINGS ======================== */}
      {activeTab === 'widget' && <WidgetSettings apiKey={apiKey} />}

      {/* ======================== CALENDAR ======================== */}
      {activeTab === 'calendar' && <Calendar />}

      {/* ======================== LEADS ======================== */}
      {activeTab === 'leads' && <Leads onNavigate={(tab) => setActiveTab(tab)} />}

      {/* ======================== LEAD CAPTURE SETTINGS ======================== */}
      {activeTab === 'lead-settings' && <LeadCaptureSettings onNavigate={(tab) => setActiveTab(tab)} />}

      {/* ======================== AI AGENTS ======================== */}
      {activeTab === 'agents' && <Agents onNavigate={(tab) => setActiveTab(tab)} />}

      {/* Render guard: double-check handoverEnabled so an inert deep-link
          like /customer-dashboard?tab=handover cannot bypass the gating. */}
      {activeTab === 'handover' && handoverEnabled && <HandoverSettings />}

      {/* ======================== CHANNELS ======================== */}
      {activeTab === 'channels' && <WhatsAppIntegration />}

      {/* ======================== INTEGRATIONS ======================== */}
      {activeTab === 'integrations' && <ZohoIntegration />}

      {/* ======================== USAGE ======================== */}
      {activeTab === 'usage' && <UsageCostPanel />}

      {/* ======================== SETTINGS ======================== */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Account info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-base font-semibold text-slate-900 mb-5">Account</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Email</label>
                <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600">{user?.email}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Account Type</label>
                <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600">Customer Account</div>
              </div>
            </div>
          </div>

          {/* Voice Toggle */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center">
                  <SpeakerWaveIcon className="h-5 w-5 text-brand-600" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-900">Voice Chat</h4>
                  <p className="text-xs text-slate-500">Enable text-to-speech responses in AI Chat</p>
                </div>
              </div>
              <button onClick={handleToggleVoice} disabled={voiceSettingsLoading}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${voiceSettingsEnabled ? 'bg-brand-600' : 'bg-slate-200'} disabled:opacity-50`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${voiceSettingsEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
            {voiceSettingsLoading && <p className="mt-2 text-xs text-slate-400 animate-pulse">Updating...</p>}
          </div>

          {/* Sign Out */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <button onClick={logout}
              className="w-full px-4 py-2.5 border border-red-200 text-red-600 text-sm font-medium rounded-xl hover:bg-red-50 transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      )}

      {/* ======================== ONBOARDING WIZARD ======================== */}
      {showOnboarding && onboardingChecked && (
        <OnboardingWizard
          user={user}
          apiKey={apiKey}
          onComplete={() => {
            setShowOnboarding(false)
            fetchInitialData()
          }}
        />
      )}

      {/* ======================== PREMIUM MODAL ======================== */}
      {showPremiumModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40" onClick={() => setShowPremiumModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-8 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <StarIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">Premium Feature</h3>
            </div>
            <div className="px-6 py-5">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <LockClosedIcon className="h-4 w-4 text-amber-500" />
                <span className="font-semibold text-slate-900">{premiumFeatureName}</span>
              </div>
              <p className="text-sm text-slate-500 text-center mb-4">
                Available for <span className="font-semibold text-amber-600">Premium</span> and <span className="font-semibold text-amber-600">Enterprise</span> users.
              </p>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-4">
                <p className="text-xs font-semibold text-amber-800 mb-2">Upgrade to unlock:</p>
                <ul className="text-xs text-amber-700 space-y-1">
                  <li>Calendar & Scheduling</li>
                  <li>Advanced AI Agents</li>
                  <li>Usage Analytics & Costs</li>
                  <li>Document Upload</li>
                </ul>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex space-x-3">
              <button onClick={() => setShowPremiumModal(false)}
                className="flex-1 px-4 py-2.5 border border-slate-200 text-sm font-medium text-slate-700 rounded-xl hover:bg-slate-50 transition-colors">
                Maybe Later
              </button>
              <button onClick={() => { setShowPremiumModal(false); setSuccess('Premium plans coming soon!'); setTimeout(() => setSuccess(''), 4000) }}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all">
                Learn More
              </button>
            </div>
          </div>
        </div>
      )}
    </CustomerLayout>
  )
}
